import { atom, getDefaultStore } from "jotai"
import { JwtPayload, jwtDecode } from "jwt-decode"
import Cookies from "js-cookie"
import { UserPermissions } from "@custom-types/user"
import { GraphBuilderPagePath, GraphBuilderPageRequiredRoles } from "@features/builder/const"
import logger from "@utils/logger"

export type User = JwtPayload & {
  email: string
  first_name: string
  last_name: string
  name: string
}

export enum AccessCode {
  OK = 200,
  FORBIDDEN = 403,
  UNAUTHORIZED = 401,
}

export type AccessStatus = {
  url: string
  code: AccessCode
}

export type ContractFamilyInfo = {
  contract_key: string
  description: string
  families: string[]
}

const getInitialUser = (): User | null => {
  const cookies = Cookies.get()
  return cookies["User"] ? jwtDecode(cookies["User"]) : null
}

const getInitialSso = (): string | null => {
  const cookies = Cookies.get()
  return cookies["SSO"] ?? null
}

export const loggedInUserAtom = atom<User | null>(getInitialUser())
export const permissionsAtom = atom<UserPermissions | undefined>(undefined)
export const accessStatusAtom = atom<AccessStatus>({ url: "", code: AccessCode.UNAUTHORIZED })

// ssoAtom holds the SSO cookie value for use in logout
export const ssoAtom = atom<string | null>(getInitialSso())

if (process.env.NODE_ENV !== "production") {
  loggedInUserAtom.debugLabel = "loggedInUserAtom"
  permissionsAtom.debugLabel = "permissionsAtom"
  accessStatusAtom.debugLabel = "accessStatusAtom"
}

const getMockedAuthSystemLogoutUrl = () => {
  const baseUrl = import.meta.env.VITE_AUTH_END_SESSION_URL
  if (!baseUrl) return ""

  // OktaEmulator is our OpenID Connect provider; these query params are part of its end-session protocol.
  const oktaEmulatorLogoutParams = new URLSearchParams()
  const sso = getDefaultStore().get(ssoAtom)
  if (sso) {
    oktaEmulatorLogoutParams.set("id_token_hint", sso)
  }

  if (import.meta.env.VITE_OKTAEMULATOR_CLIENT_ID) {
    oktaEmulatorLogoutParams.set("client_id", import.meta.env.VITE_OKTAEMULATOR_CLIENT_ID)
  }

  if (import.meta.env.VITE_LOGOUT_REDIRECT_URL) {
    oktaEmulatorLogoutParams.set(
      "post_logout_redirect_uri",
      import.meta.env.VITE_LOGOUT_REDIRECT_URL,
    )
  }

  return `${baseUrl}?${oktaEmulatorLogoutParams.toString()}`
}

const getRealAuthSystemLogoutUrl = (sso: string | null) => {
  return `${
    import.meta.env.VITE_SAM_URL
  }/oauth2/spglobal/v1/logout?id_token_hint=${sso}&post_logout_redirect_uri=${
    import.meta.env.VITE_LOGOUT_REDIRECT_URL
  }`
}

export const logout = () => {
  const sso = getDefaultStore().get(ssoAtom)
  Cookies.remove("User", {
    path: "/",
    domain: import.meta.env.VITE_COOKIE_DOMAIN,
  })
  Cookies.remove("SSO", {
    path: "/",
    domain: import.meta.env.VITE_COOKIE_DOMAIN,
  })

  // Use mocked/local auth system when configured.
  const oktaEmulatorLogoutUrl = getMockedAuthSystemLogoutUrl()
  if (oktaEmulatorLogoutUrl) {
    window.location.assign(oktaEmulatorLogoutUrl)
    return
  }

  // Otherwise, use the real auth system.
  const endSessionUrl = getRealAuthSystemLogoutUrl(sso)
  window.location.assign(endSessionUrl)
}

export const isLoggedIn = () => {
  try {
    // bug within js-cookie - DO NOT USE Cookies.get('User')-- this will cause FE looping when locally developing
    const cookie_list = Cookies.get()
    const user = jwtDecode<{ exp: string }>(cookie_list["User"] || "")

    return parseInt(user?.exp) > Math.floor(Date.now() / 1000)
  } catch (exceptionVar) {
    logger.debug(exceptionVar)
    return false
  }
}

export const hasAccess = (permissions: UserPermissions | undefined, uri: string) => {
  if (!permissions) {
    return false
  }

  if (!Object.keys(permissions).length) {
    return false
  }

  if (permissions.admin?.length) {
    return true
  }

  if (uri === "/") {
    return true
  }

  if (uri.startsWith("/Data")) {
    return !!permissions.build || !!permissions.support
  }

  if (uri.startsWith("/Calibrate")) {
    return !!permissions.build || !!permissions.support
  }

  if (uri === GraphBuilderPagePath) {
    return GraphBuilderPageRequiredRoles.some((role) => !!permissions[role])
  }

  if (uri.startsWith("/ScenarioDesign")) {
    return !!permissions.whatif || !!permissions.build
  }

  if (uri.startsWith("/Run")) {
    return !!permissions.support || !!permissions.whatif || !!permissions.build
  }

  if (uri.startsWith("/Results")) {
    return (
      !!permissions.whatif || !!permissions.viewer || !!permissions.support || !!permissions.build
    )
  }

  if (uri.startsWith("/Admin")) {
    return !!permissions.admin
  }

  return false
}
